import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';

import { MingaPermission } from 'libs/domain';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, switchMap, takeUntil } from 'rxjs/operators';

import { PermissionsService } from '@app/src/app/permissions';

import { LayoutService } from '@modules/layout/services';

import { CarouselStylePresets } from '@shared/components/tiles-carousel';
import { QueryParamService } from '@shared/services/filtering/query-param.service';
import { FlexTimePeriodService } from '@shared/services/flex-time';
import { HallPassService } from '@shared/services/hall-pass';

import { DEFAULT_ROUTE, SelectionAssignerRoutes } from './constants';
import { SelectionAssignerService } from './services';
import {
  BaseSaPageData,
  CarouselTileData,
  FilterSaPageData,
  SaAssignOverlayConfig,
  SaSelectorTypes,
  SelectionAssignerQueryParams,
  selectionAssignerSchema,
  TabSaPageData,
} from './types';

@Component({
  selector: 'mg-selection-assigner',
  templateUrl: './selection-assigner.component.html',
  styleUrls: ['./selection-assigner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SelectionAssignerService, HallPassService, FlexTimePeriodService, QueryParamService],
})
export class SelectionAssignerComponent implements OnDestroy {
  @ViewChild('assignerContainer', { static: false, read: ElementRef })
  private _assignerContainer: ElementRef;
  @ViewChild('assignerForm', { static: false, read: ElementRef })
  private _assignerForm: ElementRef;

  /** General Observables */
  public presetCarouselLayout$: Observable<CarouselStylePresets>;
  public carouselList$: Observable<CarouselTileData[]>;
  public media$: Observable<string>;
  private _destroyedSubject = new ReplaySubject<void>(1);

  /** Form Controls */
  public typeControl = this.saService.typeFormControl;

  /** Const */
  public modalConfig: SaAssignOverlayConfig;
  public pageData: BaseSaPageData | TabSaPageData;

  /** Other */
  public submitButtonLabel: 'Next' | 'Create' = 'Next';
  public filterMsgs: FilterSaPageData = {
    filterPlaceholder: '',
    filterEmptyHeading: '',
    filterEmptyMsg: '',
  };

  /** Component Constructor */
  constructor(
    public saService: SelectionAssignerService,
    private _router: Router,
    private _location: Location,
    private _mediaObserver: MediaObserver,
    private _activatedRoute: ActivatedRoute,
    private _permissions: PermissionsService,
    private _cdr: ChangeDetectorRef,
    public layoutService: LayoutService,
    private _queryParamService: QueryParamService<SelectionAssignerQueryParams>,
  ) {
    combineLatest([
      this._router.events.pipe(
        // prior to Angular 15 these we're coming through as NavigationEnd instance even though they were Scroll events
        filter(event => event instanceof NavigationEnd || event instanceof Scroll),
        map(event => (event instanceof Scroll ? (event.routerEvent as NavigationEnd) : (event as NavigationEnd))),
      ),
      this._queryParamService.useQueryParams(selectionAssignerSchema.parse),
    ])
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(([data, queryParams]) => {
        this.saService.setImportBehaviors(queryParams.importBehaviors);

        const activeSelector = this._getActiveType(data.url, queryParams);
        this.saService.activeSelectorSubject.next(activeSelector);
      });

    this.saService.activeSelector$
      .pipe(
        takeUntil(this._destroyedSubject),
        filter(selector => !!selector),
      )
      .subscribe(selector => {
        this._reset();
        this._setModalConfig();
      });

    this.presetCarouselLayout$ = this._mediaObserver.asObservable().pipe(
      map(change => change[0].mqAlias),
      distinctUntilChanged(),
      switchMap(media => {
        return ['xs', 'sm'].includes(media)
          ? of(CarouselStylePresets.VERT_LIST)
          : of(CarouselStylePresets.THREE_X_FOUR);
      }),
    );

    this.media$ = this._mediaObserver.asObservable().pipe(
      takeUntil(this._destroyedSubject),
      map(change => change[0].mqAlias),
      distinctUntilChanged(),
    );

    this.typeControl.valueChanges.pipe(takeUntil(this._destroyedSubject), skip(1)).subscribe(() => {
      this._scrollToBottom();
    });

    this.carouselList$ = this.saService.carouselData$;
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  public back() {
    if (this._router.lastSuccessfulNavigation?.previousNavigation.finalUrl.toString() === DEFAULT_ROUTE)
      this._router.navigateByUrl('/');
    else this._location.back();
  }

  public close() {
    this._router.navigate([{ outlets: { modal: null } }]);
  }

  public filterValueSelected(data: CarouselTileData[]) {
    this.saService.carouselDataSubject.next(data);
  }

  public async submit() {
    const params = this._queryParamService.getQueryParamSnapshot(
      this._activatedRoute.snapshot,
      selectionAssignerSchema.parse,
    );
    await this.saService.submit(params.originUrl);
  }

  private _getActiveType(url: string, queryParams: SelectionAssignerQueryParams): SaSelectorTypes {
    const defaultTab = {
      [SelectionAssignerRoutes.HALL_PASS]: 'hall-pass',
      [SelectionAssignerRoutes.BEHAVIOR]: 'praise',
      [SelectionAssignerRoutes.CHECK_IN]: 'checkin',
    };

    const urlWithoutParams = url.split('?')[0];
    const assignerCategory = urlWithoutParams?.split('/')?.pop().replace(')', '');
    const fallback = defaultTab[assignerCategory] || 'hall-pass';
    // active tab query params takes precedence, fallback is segment defined in url
    const activeType = queryParams.tab || fallback;
    return activeType as SaSelectorTypes;
  }

  private async _setModalConfig() {
    this.modalConfig = this.saService.getModalConfig();
    const isSelfAssign = !this._permissions.hasPermission(MingaPermission.HALL_PASS_MANAGE);

    if (isSelfAssign) this.submitButtonLabel = 'Create';
    if (isSelfAssign && this.modalConfig.selfAssignTitle) {
      this.modalConfig.formTitle = this.modalConfig.selfAssignTitle;
    }

    if (this.modalConfig?.tabbedHeader) {
      const pagesData = this.modalConfig?.data as TabSaPageData[];
      if (pagesData) {
        const activePageData = pagesData.find(data => data.selector === this.saService.activeSelectorSubject.value);
        this.pageData = activePageData;
      }
    } else {
      this.pageData = this.modalConfig?.data as BaseSaPageData;
    }

    const route = this._activatedRoute.snapshot.firstChild.routeConfig.path as SelectionAssignerRoutes;

    if (route === SelectionAssignerRoutes.BEHAVIOR) {
      const canCreatePbis = this._permissions.hasPermission(MingaPermission.PBIS_CREATE);
      const canCreateConsequence = this._permissions.hasPermission(MingaPermission.CONSEQUENCE_CREATE);
      const canManagePbis = this._permissions.hasPermission(MingaPermission.PBIS_TYPE_MANAGE);

      const canCreate = canCreateConsequence || canManagePbis;

      if (!canCreate && Array.isArray(this.modalConfig.data)) {
        this.modalConfig.data = this.modalConfig.data?.filter(tab => tab.selector !== 'consequence');
        this._cdr.markForCheck();
      }
      if (!canCreatePbis && Array.isArray(this.modalConfig.data)) {
        this.modalConfig.data = this.modalConfig.data?.filter(
          tab => tab.selector !== 'praise' && tab.selector !== 'guidance',
        );
        this.modalConfig.formTitle = 'Assign Consequence';
        this._cdr.markForCheck();
      }
    }

    this.filterMsgs = this.pageData?.filterInfo;
  }

  public changeTab(tab: SaSelectorTypes) {
    this._queryParamService.updateQueryParams({
      tab,
    });
  }

  private _reset() {
    this.saService.reset();
  }

  private _scrollToBottom() {
    const element = this._assignerContainer?.nativeElement;
    if (!element && element?.scrollTop !== 0) return;

    element?.scrollIntoView({
      behavior: 'smooth',
    });
  }
}
